import React from "react";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, User, Mail, Shield, Key } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export function AdminAccount() {
  const { user } = useAuth();

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-xl font-bold tracking-tight text-[#1C1924]">
          Account Security
        </h2>
        <p className="text-sm text-[#4A4652] mt-1">
          Manage your administrator profile and security settings.
        </p>
      </div>

      <div className="bg-white border border-[#E8DFD0] rounded-2xl overflow-hidden shadow-sm">
        <div className="p-6 border-b border-[#E8DFD0]">
          <h3 className="font-bold text-[#1C1924] flex items-center gap-2">
            <User size={18} className="text-[#8b8790]" />
            Profile Information
          </h3>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex items-center gap-4">
            <img 
              src={user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "Admin")}&background=1C1924&color=fff`}
              alt="Avatar"
              className="w-16 h-16 rounded-full object-cover border border-[#E8DFD0]"
            />
            <div>
              <p className="text-lg font-bold text-[#1C1924]">{user?.name || "Admin"}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge tone="success">
                  <Shield size={10} className="mr-1 inline" /> {user?.role || "Administrator"}
                </Badge>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-[#f1f5f9]">
            <div>
              <label className="text-[12px] font-bold uppercase tracking-wider text-[#8b8790] block mb-1">
                Full Name
              </label>
              <div className="bg-[#FAF8F5] px-4 py-2.5 rounded-xl text-[14px] font-medium text-[#1C1924] border border-[#E8DFD0]">
                {user?.name || "Not set"}
              </div>
            </div>
            <div>
              <label className="text-[12px] font-bold uppercase tracking-wider text-[#8b8790] block mb-1 flex items-center gap-1">
                Email Address <ShieldCheck size={14} className="text-green-600" />
              </label>
              <div className="bg-[#FAF8F5] px-4 py-2.5 rounded-xl text-[14px] font-medium text-[#1C1924] border border-[#E8DFD0] flex items-center gap-2">
                <Mail size={16} className="text-[#8b8790]" />
                {user?.email || "Not set"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E8DFD0] rounded-2xl overflow-hidden shadow-sm">
        <div className="p-6 border-b border-[#E8DFD0] flex items-center justify-between">
          <h3 className="font-bold text-[#1C1924] flex items-center gap-2">
            <Key size={18} className="text-[#8b8790]" />
            Authentication Methods
          </h3>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between p-4 bg-[#FAF8F5] rounded-xl border border-[#E8DFD0]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-[#e2e8f0]">
                <img src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5" alt="Google" />
              </div>
              <div>
                <p className="font-bold text-[14px] text-[#1C1924]">Google Auth</p>
                <p className="text-[12px] text-[#8b8790]">Signed in via Google Workspace</p>
              </div>
            </div>
            <Badge tone="success">Active</Badge>
          </div>
          
          <p className="text-[13px] text-[#8b8790] mt-4 flex items-center gap-2">
            <ShieldCheck size={16} />
            For security reasons, your admin access level requires authentication via Google. Passwords cannot be manually changed here.
          </p>
        </div>
      </div>
    </div>
  );
}
